/**
 * Co-pilot scoped actions — allow-list & validators (Phase 6, Feb 2026).
 *
 * Phase 5 made the Co-pilot read-only. Phase 6 lets it propose CONCRETE
 * actions that the user can accept by clicking a chip in the chat. This
 * file is the single source of truth for what the Co-pilot is allowed to
 * touch — anything outside this allow-list is silently dropped, so we
 * never let the LLM mutate something dangerous like deleting a scene or
 * pushing a payment.
 *
 * How it works
 * ────────────
 *   • The LLM (admin backend) returns `{ answer, actions: [...] }`.
 *   • Each action is `{ id, label, type, payload }`.
 *   • `validateAction(action, currentAppStep)` strips anything not in the
 *     step's allow-list AND verifies the payload shape.
 *   • CoPilotPanel renders the surviving actions as chips below the answer.
 *   • Clicking a chip → App.js `handleCoPilotAction(action)` applies it.
 *
 * Destructive actions
 * ───────────────────
 *   Mark them with `destructive: true`. CoPilotPanel pops a confirm modal
 *   showing a before/after preview before applying.
 */

// ─── Allow-list per wizard step ────────────────────────────────────────
const ALLOWED_ACTIONS_PER_STEP = {
    project:    ['project.rename', 'project.set_duration', 'project.set_aspect_ratio', 'project.set_tier'],
    script:     ['script.replace', 'script.trim', 'script.change_tone'],
    concept:    [],
    characters: ['characters.add_narrator', 'characters.add_character', 'characters.remove', 'characters.update'],
    branding:   ['branding.toggle_logo', 'branding.toggle_outro', 'branding.set_logo_position'],
    tier:       ['tier.switch'],
    storyboard: [],
    studio:     [],
    dashboard:  [],
};

// ─── Payload validators (one per action type) ──────────────────────────
const isNonEmptyString = (v) => typeof v === 'string' && v.trim().length > 0;
const isString         = (v) => typeof v === 'string';
const isFiniteNum      = (v) => typeof v === 'number' && Number.isFinite(v);

const VALID_DURATIONS  = new Set(['15 seconds', '30 seconds', '45 seconds', '60 seconds', '90 seconds']);
const VALID_RATIOS     = new Set(['16:9', '9:16', '1:1', '4:5']);
const VALID_TIERS      = new Set(['economy', 'standard', 'premium']);
const VALID_TONES      = new Set(['formal', 'casual', 'playful', 'punchy', 'cinematic']);
const VALID_POSITIONS  = new Set(['top-left', 'top-right', 'bottom-left', 'bottom-right', 'center']);

const ACTION_SPEC = {
    'project.rename': {
        destructive: false,
        validate: (p) => isNonEmptyString(p?.value),
        format: (p) => `Rename project to "${p.value}"`,
    },
    'project.set_duration': {
        destructive: false,
        validate: (p) => VALID_DURATIONS.has(p?.value),
        format: (p) => `Set duration to ${p.value}`,
    },
    'project.set_aspect_ratio': {
        destructive: false,
        validate: (p) => VALID_RATIOS.has(p?.value),
        format: (p) => `Switch to ${p.value}`,
    },
    'project.set_tier': {
        destructive: false,
        validate: (p) => VALID_TIERS.has(p?.value),
        format: (p) => `Switch to ${p.value.charAt(0).toUpperCase() + p.value.slice(1)} tier`,
    },
    'script.replace': {
        destructive: true,
        validate: (p) => isNonEmptyString(p?.value) && p.value.length <= 10000,
        format:   () => 'Replace the script',
    },
    'script.trim': {
        destructive: true,
        validate: (p) => isFiniteNum(p?.target_seconds) && p.target_seconds >= 5 && p.target_seconds <= 180,
        format:   (p) => `Trim script to ~${p.target_seconds}s`,
    },
    'script.change_tone': {
        destructive: true,
        validate: (p) => VALID_TONES.has(p?.tone),
        format:   (p) => `Rewrite in ${p.tone} tone`,
    },
    'characters.add_narrator': {
        destructive: false,
        validate: () => true,
        format:   () => 'Add a narrator',
    },
    'characters.add_character': {
        destructive: false,
        validate: (p) => isNonEmptyString(p?.name),
        format:   (p) => `Add character "${p.name}"`,
    },
    'characters.remove': {
        destructive: true,
        validate: (p) => isNonEmptyString(p?.id),
        format:   (p) => p?.name ? `Remove "${p.name}"` : 'Remove cast member',
    },
    'characters.update': {
        destructive: false,
        validate: (p) => isNonEmptyString(p?.id) && (isString(p?.name) || isString(p?.description)),
        format:   (p) => p?.name ? `Update "${p.name}"` : 'Update cast member',
    },
    'branding.toggle_logo': {
        destructive: false,
        validate: (p) => typeof p?.enabled === 'boolean',
        format:   (p) => p.enabled ? 'Show your logo on every frame' : 'Hide the logo',
    },
    'branding.toggle_outro': {
        destructive: false,
        validate: (p) => typeof p?.enabled === 'boolean',
        format:   (p) => p.enabled ? 'Add an outro card' : 'Remove the outro card',
    },
    'branding.set_logo_position': {
        destructive: false,
        validate: (p) => VALID_POSITIONS.has(p?.value),
        format:   (p) => `Move logo to ${p.value.replace('-', ' ')}`,
    },
    'tier.switch': {
        destructive: false,
        validate: (p) => VALID_TIERS.has(p?.value),
        format:   (p) => `Switch to ${p.value.charAt(0).toUpperCase() + p.value.slice(1)} tier`,
    },
};

/**
 * Validate an action against the current step's allow-list. Returns the
 * normalised action (with a generated `id` and computed `label`/`destructive`
 * filled in) or `null` if the action is not allowed.
 */
export const validateAction = (raw, currentAppStep) => {
    if (!raw || typeof raw !== 'object') return null;
    const { type, payload = {} } = raw;
    if (!isNonEmptyString(type)) return null;

    const stepAllowed = ALLOWED_ACTIONS_PER_STEP[currentAppStep] || [];
    if (!stepAllowed.includes(type)) return null;

    const spec = ACTION_SPEC[type];
    if (!spec) return null;
    if (!spec.validate(payload)) return null;

    return {
        id:          isNonEmptyString(raw.id) ? raw.id : `${type}-${Math.random().toString(36).slice(2, 8)}`,
        label:       isNonEmptyString(raw.label) ? raw.label : spec.format(payload),
        type,
        payload,
        destructive: spec.destructive,
    };
};

export const validateActions = (rawList, currentAppStep) => {
    if (!Array.isArray(rawList)) return [];
    return rawList.map(a => validateAction(a, currentAppStep)).filter(Boolean);
};

export const isStepActionable = (currentAppStep) =>
    (ALLOWED_ACTIONS_PER_STEP[currentAppStep] || []).length > 0;
